import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AddUnitOperationLinksModal from 'studies/typed/shared/flowcharts/unit-op-linking/AddUnitOperationLinksModal';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';

import {
  LinkToExistingUnitOperationsButtonQuery,
  LinkToExistingUnitOperationsButtonQueryVariables,
} from './__generated/LinkToExistingUnitOperationsButtonQuery';

export const LINK_TO_EXISTING_UNIT_OPERATIONS_BUTTON_QUERY = gql`
  query LinkToExistingUnitOperationsButtonQuery($studyId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        procedureRunCount
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type StudyNode = ExtractByTypename<LinkToExistingUnitOperationsButtonQuery['study'], 'StudyNode'>;

interface ButtonProps {
  disabled: boolean;
  tooltipText: string | null;
}
interface LinkToExistingUnitOperationsButtonProps {
  studyId: string;
  procedureRunId: string;
  procedureRunName: string;
  studyType: StudyType;
}

export default function LinkToExistingUnitOperationsButton(
  props: LinkToExistingUnitOperationsButtonProps
): JSX.Element {
  const studyId = props.studyId;
  const result = useBenchlingQuery<
    LinkToExistingUnitOperationsButtonQuery,
    LinkToExistingUnitOperationsButtonQueryVariables
  >(LINK_TO_EXISTING_UNIT_OPERATIONS_BUTTON_QUERY, {
    variables: {studyId},
    renderCachedDataWhenRefetching: true,
  });

  const buttonProps: ButtonProps = {disabled: true, tooltipText: null};
  let onClickHandler: () => void = noop;

  if (result.type === QueryResultType.PENDING) {
    buttonProps.tooltipText = 'Loading...';
  } else if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.study)) {
    const study: StudyNode = result.data.study;
    buttonProps.disabled = study.procedureRunCount === 1;
    buttonProps.tooltipText = `No existing ${getProcedureRunUIStringForStudyType(props.studyType, {plural: true})} to link to`;
  }
  assert(props.procedureRunId != null, 'Procedure Run is not selected');
  assert(props.procedureRunName != null, 'Procedure Run is not selected');
  onClickHandler = async () => {
    await legacyRunModal(AddUnitOperationLinksModal, {
      studyId: props.studyId,
      procedureRunId: props.procedureRunId,
      procedureRunName: props.procedureRunName,
    });
  };
  return (
    <Tooltip isDisabled={!buttonProps.disabled} text={buttonProps.tooltipText}>
      <Button
        dataTestAttrs={{component: 'LinkToExistingButton', element: 'button'}}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MEDIUM}
        onClick={onClickHandler}
        text="Link to existing"
        icon="diagram-nested"
        disabled={buttonProps.disabled}
      />
    </Tooltip>
  );
}
